import { z } from 'zod';
import { FeeStatus } from '../enums';
import { ApplicantOfferPaymentMethod, ApplicantOfferPaymentSponsorType, ApplicantOfferPaymentStatus } from './enums';

export const DepositAmountDetailSchema = z
  .object({
    depositAmount: z
      .number({
        message: 'Please provide the amount'
      })
      .min(0, 'Please provide the amount'),
    paymentDate: z
      .string({
        message: 'Please provide the date'
      })
      .date()
  })
  .superRefine((depositDetail, ctx) => {
    const { paymentDate } = depositDetail;
    const dateOfPayment = new Date(paymentDate);
    const today = new Date();

    if (dateOfPayment > today) {
      ctx.addIssue({
        code: 'custom',
        path: ['paymentDate'],
        message: 'Payment date should not be future date'
      });
    }
  });

export const SponsorshipDetailSchema = z
  .object({
    sponsorType: z.nativeEnum(ApplicantOfferPaymentSponsorType, { message: 'Please provide the type' }),
    sponsorName: z.string({ message: 'Please provide the name' }).min(1, 'Please provide the name'),
    isSponsorOrgMultinational: z.boolean().optional().nullish()
  })
  .superRefine((sponsorshipDetail, ctx) => {
    const { sponsorType, isSponsorOrgMultinational } = sponsorshipDetail;
    if (
      [
        ApplicantOfferPaymentSponsorType.InternationalOrganisation,
        ApplicantOfferPaymentSponsorType.InternationalCompany
      ].includes(sponsorType) &&
      typeof isSponsorOrgMultinational !== 'boolean'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['isSponsorOrgMultinational'],
        message: 'Provide sponsor global presence information'
      });
    }
  });

const crnMessage = 'Please enter a valid Customer Reference Number (CRN) with 11 digits';
const ssnMessage = 'Please enter a valid Student Support Number (SSN) with 13 digits';
export const SLCDetailSchema = z.object({
  customerRefNumber: z.string({ message: crnMessage }).length(11, crnMessage).regex(/^\d+$/, crnMessage),
  studentSupportNumber: z.string({ message: ssnMessage }).length(13, ssnMessage).regex(/^\d+$/, ssnMessage)
});

const defaultRefineFn = (paymentDetail: IApplicantOfferPayment, ctx: z.RefinementCtx) => {
  const { paymentMethod, depositDetail, sponsorshipDetail, slcDetail } = paymentDetail;
  if (paymentMethod === ApplicantOfferPaymentMethod.Deposit && !depositDetail) {
    ctx.addIssue({
      code: 'custom',
      path: ['depositDetail'],
      message: 'Provide deposit detail'
    });
  } else if (paymentMethod === ApplicantOfferPaymentMethod.Sponsorship && !sponsorshipDetail) {
    ctx.addIssue({
      code: 'custom',
      path: ['sponsorshipDetail'],
      message: 'Provide sponsorship detail'
    });
  } else if (paymentMethod === ApplicantOfferPaymentMethod.SLCFunding && !slcDetail) {
    ctx.addIssue({
      code: 'custom',
      path: ['slcDetail'],
      message: 'Provide student loan company details'
    });
  }
};
export const ProofOfPaymentSchema = z.object({
  fileName: z.string({ message: 'Please provide the file name' }),
  s3Key: z.string(),
  sKey: z.string(),
  urls: z.array(z.string()).optional(),
  status: z.nativeEnum(ApplicantOfferPaymentStatus).optional().nullish(),
  statusInternalNote: z.string().optional().nullish()
});

export const ApplicantOfferPaymentSchema = z
  .object({
    paymentMethod: z.nativeEnum(ApplicantOfferPaymentMethod, { message: 'Please provide the value' }),
    depositDetail: DepositAmountDetailSchema.optional().nullish(),
    sponsorshipDetail: SponsorshipDetailSchema.optional().nullish(),
    slcDetail: SLCDetailSchema.optional().nullish(),
    proofOfPayment: ProofOfPaymentSchema.optional().nullish(),
    status: z.nativeEnum(ApplicantOfferPaymentStatus).optional().nullish(),
    statusInternalNote: z.string().optional().nullish()
  })
  .superRefine((paymentDetail, ctx) => {
    defaultRefineFn(paymentDetail, ctx);
  });

export function enhanceOfferPaymentSchema(feeStatus: FeeStatus) {
  if (feeStatus !== FeeStatus.Home) {
    return ApplicantOfferPaymentSchema.superRefine((data, ctx) => {
      const { paymentMethod } = data;

      defaultRefineFn(data, ctx);
      if (paymentMethod === ApplicantOfferPaymentMethod.SLCFunding) {
        ctx.addIssue({
          code: 'custom',
          path: ['slcDetail'],
          message: 'Only home student can apply for student loan company'
        });
      }
    });
  }
  return ApplicantOfferPaymentSchema;
}

export const UpdateApplicantOfferPayment = z.object({
  status: z.nativeEnum(ApplicantOfferPaymentStatus),
  statusInternalNote: z.string().optional()
});

/* eslint-disable @typescript-eslint/no-empty-object-type */

export interface IDepositAmountDetail extends z.infer<typeof DepositAmountDetailSchema> {}
export interface ISponsorshipDetail extends z.infer<typeof SponsorshipDetailSchema> {}
export interface ISLCDetail extends z.infer<typeof SLCDetailSchema> {}
export interface IApplicantOfferPayment extends z.infer<typeof ApplicantOfferPaymentSchema> {}
export interface IApplicantUpdateOfferPaymentStatus extends z.infer<typeof UpdateApplicantOfferPayment> {}
export interface IProofOfPayment extends z.infer<typeof ProofOfPaymentSchema> {}
